import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight, Gift, MessageCircle } from "lucide-react";
import { img } from "../lib/api";
import { useSite } from "../lib/store";
import { Dots, sections } from "../components/Bits";
import { useSeo } from "../components/Seo";

export const TINTS = ["var(--pink-soft)", "var(--teal-soft)", "var(--sun-soft)", "var(--lilac-soft)"];
export const INKS = ["var(--pink-dark)", "#2b8f8a", "#a07e00", "#8a5cc2"];

export function PageBanner({ eyebrow, title, text, image, children, testid }) {
  return (
    <section className="relative overflow-hidden paper" data-testid={testid}>
      <Dots />
      <div className={`max-w-7xl mx-auto px-6 py-14 lg:py-20 grid ${image ? "lg:grid-cols-[1.1fr_1fr]" : ""} gap-10 items-center`}>
        <motion.div className={image ? "" : "text-center max-w-3xl mx-auto"} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="eyebrow mb-3">{eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold"><span className="squiggle">{title}</span></h1>
          <p className={`mt-6 text-base md:text-lg text-[var(--muted)] max-w-xl leading-relaxed ${image ? "" : "mx-auto"}`}>{text}</p>
          {children}
        </motion.div>
        {image && <motion.div animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: 0.15 }} className="relative mx-auto w-full max-w-[460px]">
          <span className="absolute -inset-3 rounded-[48px] border-2 border-dashed border-[var(--pink)] opacity-60 rotate-2" />
          <img src={img(image)} alt="" className="relative w-full aspect-[4/3.4] object-cover rounded-[40px] border-8 border-white shadow-[0_30px_60px_-25px_rgba(212,86,140,.45)]" data-testid={`${testid}-image`} />
        </motion.div>}
      </div>
    </section>
  );
}

function Steps({ text }) {
  const steps = sections(text);
  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="how-steps">
      <p className="eyebrow mb-2 text-center">Postup</p>
      <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-14"><span className="squiggle">Čtyři kroky k radosti</span></h2>
      <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <svg className="hidden lg:block absolute top-10 left-[12%] w-[76%] h-6" viewBox="0 0 400 20" preserveAspectRatio="none" aria-hidden><path d="M0 10 C 50 0, 80 20, 130 10 S 220 0, 270 10 S 350 20, 400 10" fill="none" stroke="var(--pink)" strokeWidth="2" strokeDasharray="6 7" /></svg>
        {steps.map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative text-center" data-testid={`how-step-${i}`}>
            <span className="relative mx-auto w-20 h-20 rounded-full grid place-items-center font-display text-3xl border-4 border-white shadow-md" style={{ background: TINTS[i % 4], color: INKS[i % 4] }}>{i + 1}</span>
            <div className="mt-5 rounded-[28px] p-6 bg-white border border-[#f6e3ec] h-[calc(100%-6.25rem)]">
              <h3 className="font-display text-xl">{s.title}</h3>
              <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed whitespace-pre-line">{s.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Faq({ text }) {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-4xl mx-auto px-6 py-16" data-testid="faq-section">
      <p className="eyebrow mb-2 text-center">Časté dotazy</p>
      <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-10"><span className="squiggle">Na co se ptáte</span></h2>
      <div className="space-y-3">
        {sections(text).map((q, i) => (
          <div key={i} className={`rounded-[24px] border transition-colors ${open === i ? "bg-[var(--blush)] border-[#f3d3e1]" : "bg-white border-[#f6e3ec]"}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5" data-testid={`faq-question-${i}`}>
              <span className="font-display text-lg">{q.title}</span>
              <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center transition-transform ${open === i ? "rotate-45 bg-[var(--pink)] text-white" : "bg-[var(--pink-soft)] text-[var(--pink-dark)]"}`}><Plus className="w-4 h-4" /></span>
            </button>
            <AnimatePresence initial={false}>
              {open === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <p className="px-6 pb-5 text-[#4a4752] leading-relaxed whitespace-pre-line" data-testid={`faq-answer-${i}`}>{q.body}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
}

export function CtaBand() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-10">
      <div className="relative overflow-hidden rounded-[36px] bg-[var(--ink)] text-white p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <Dots />
        <div className="relative">
          <h3 className="text-3xl font-semibold">Máte ještě otázku?</h3>
          <p className="text-white/70 mt-2">Napište nám, rády poradíme s výběrem i s nákupem.</p>
        </div>
        <div className="relative flex flex-wrap gap-3">
          <Link to="/kontakt" className="btn-pink" data-testid="cta-contact-btn"><MessageCircle className="w-4 h-4" /> Napsat zprávu</Link>
          <Link to="/eshop" className="btn-pill bg-white text-[var(--ink)] hover:-translate-y-0.5" data-testid="cta-shop-btn">Do e-shopu <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  const { site } = useSite();
  useSeo({ title: site?.texts.how_hero_title || "Jak to funguje", description: site?.texts.how_hero_text, image: site?.texts.how_banner });
  if (!site) return <div className="h-[60vh]" />;
  const t = site.texts;
  return (
    <div data-testid="how-page">
      <PageBanner testid="how-banner" eyebrow="Jednoduše a rychle" title={t.how_hero_title} text={t.how_hero_text} image={t.how_banner}>
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to="/eshop" className="btn-pink">Vybrat materiály <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/kategorie/materialy-zdarma" className="btn-ghost"><Gift className="w-4 h-4" /> Zdarma za odběr</Link>
        </div>
      </PageBanner>
      <Steps text={t.how_it_works} />
      <Faq text={t.faq} />
      <CtaBand />
    </div>
  );
}
